// Types pour l'intégration des paiements (FeexPay)

export type PaymentNetwork = "FLOOZ" | "TMONEY";

export enum PaymentMethod {
  CASH = 'CASH',
  FLOOZ = 'FLOOZ',
  TMONEY = 'TMONEY',
  WAVE = 'WAVE',
  BANK_TRANSFER = 'BANK_TRANSFER',
  OTHER = 'OTHER',
}

export type PayoutRequestStatus = "PENDING" | "APPROVED" | "REJECTED" | "PAID";

export type PayoutRequestType = "USER" | "MODERATOR";

// ==========================================
// BUDGET
// ==========================================

export interface BackendBudgetResponse {
  budget: number;
  reserved: number;
  available: number;
  currency: string;
  lastUpdated: string;
}

export interface BackendPaymentStats {
  totalPaid: number;
  totalPending: number;
  pendingTransactions: number;
  pendingRequestsCount: number;
  pendingRequestsAmount: number;
  totalRequests: number;
  balance: number;
  successfulTransactions: number;
  failedTransactions: number;
}

export interface BackendPaymentSettings {
  contributorAmount: number;
  contributorCap: number;
  contributorBudget: number;
  moderatorAmount: number;
  moderatorCount: number;
  autoPayModerators: boolean;
  communityCompensationBasis: CommunityCompensationBasis;
  communityAmountPerContribution: number;
  communityAmountPerRecordedMinute: number;
}

export type CommunityCompensationBasis = "contribution" | "recorded_minute" | "hybrid";

export interface BackendDepositPayload {
  amount: number;
  phone: string;
  network: PaymentNetwork;
  email?: string;
  firstName?: string;
  lastName?: string;
  description?: string;
}

export interface BackendDepositResponse {
  success: boolean;
  txReference: string;
  identifier: string;
  message: string;
}

export type DepositTransactionStatus =
  | "CREATED"
  | "PENDING"
  | "SUCCESS"
  | "FAILED";

export interface BackendDepositStatusResponse {
  status: DepositTransactionStatus;
  identifier: string;
  amount: number;
  amountPaid?: number;
}

// ==========================================
// PAYOUTS
// ==========================================

export interface BackendPayoutUserPayload {
  userId: number;
  phone: string;
  amount: number;
  network: PaymentNetwork;
  description?: string;
}

export interface BackendPayoutModeratorPayload {
  moderatorId: string;
  phone: string;
  amount: number;
  network: PaymentNetwork;
  description?: string;
}

export interface BackendPayoutResponse {
  success: boolean;
  tx_reference: string;
  amount: number;
  recipient: string;
  network: PaymentNetwork;
  status: string;
  message: string;
  createdAt: string;
}

// ==========================================
// PAYOUT REQUESTS
// ==========================================

export interface BackendCreateRequestPayload {
  userId: number;
  amount: number;
  phone: string;
  network: PaymentNetwork;
  description?: string;
  requestType?: PayoutRequestType;
}

export interface BackendCreateRequestResponse {
  id: string;
  userId: number;
  amount: number;
  phone: string;
  network: PaymentNetwork;
  status: PayoutRequestStatus;
  description?: string;
  requestType: PayoutRequestType;
  createdAt: string;
}

export interface BackendPayoutRequest {
  id: string;
  userId: number;
  userName?: string;
  userEmail?: string;
  amount: number;
  phone: string;
  phone_number?: string;
  network: PaymentNetwork;
  status: PayoutRequestStatus;
  description?: string;
  requestType?: PayoutRequestType;
  moderatorNote?: string;
  validatedBy?: string;
  validatedAt?: string;
  paidAt?: string;
  txReference?: string;
  createdAt: string;
  updatedAt: string;
}

export interface BackendGetRequestsResponse {
  items: BackendPayoutRequest[];
  total: number;
  page: number;
  limit: number;
}

export interface BackendValidateRequestPayload {
  action: "APPROVED" | "REJECTED";
  note?: string;
}

export interface BackendAccumulatedAmountResponse {
  amount: number;
  currency?: string;
  lastPayoutDate?: string;
  availableAt?: string;
}

export interface BackendAdminPayoutRequestsResponse {
  moderatorRequests: BackendPayoutRequest[];
  contributorRequests: BackendPayoutRequest[];
  moderatorTotal: number;
  contributorTotal: number;
}

// ==========================================
// UI TYPES
// ==========================================

export interface PayoutSettings {
  contributorAmount: number;
  contributorCap: number;
  contributorBudget: number;
  moderatorAmount: number;
  moderatorCount: number;
  autoPayModerators: boolean;
}

export interface UserForPayout {
  id: string;
  username: string;
  email?: string;
  phoneNumber?: string;
  score: number;
  validatedTests: number;
  amount: number;
  hasPaymentInfo: boolean;
}

export interface ModeratorForPayout {
  id: string;
  name: string;
  email: string;
  phoneNumber?: string;
  validatedCount: number;
  earnedAmount: number;
  lastPayoutDate?: string;
}

// ==========================================
// ADMIN PAYMENT SETTINGS (from backend)
// ==========================================

export interface AdminPaymentSettings {
  amountPerContribution: number;
  contributorCap: number;
  moderatorAmount: number;
}

export interface CreatePayDto {
  amountPerContribution: number;
  contributorCap: number;
  moderatorAmount: number;
}

// ==========================================
// DEPOSIT (Admin deposits via FeexPay)
// ==========================================

export interface AdminDepositPayload {
  amount: number;
  phone: string;
  email: string;
  firstName: string;
  lastName: string;
  description?: string;
}

export interface AdminDepositResponse {
  success: boolean;
  txReference: string;
  identifier: string;
  payment_url: string;
  message: string;
}

// ==========================================
// PAYOUT USER (Admin pays contributor)
// ==========================================

export interface PayoutUserPayload {
  userId: number;
  amount: number;
  phone: string;
  network: PaymentNetwork;
  description?: string;
}

// ==========================================
// PAYOUT MODERATOR (Admin pays moderator)
// ==========================================

export interface PayoutModeratorPayload {
  moderatorId: number;
  phone: string;
  network: PaymentNetwork;
  description?: string;
}

// ==========================================
// TRANSACTION TYPES
// ==========================================

export enum TransactionType {
  DEPOSIT = 'DEPOSIT',
  PAYOUT_USER = 'PAYOUT_USER',
  PAYOUT_MODERATOR = 'PAYOUT_MODERATOR',
}

export enum PaymentStatus {
  CREATED = 'CREATED',
  PENDING = 'PENDING',
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
}

// ==========================================
// REQUEST OPERATIONS
// ==========================================

export interface ValidateRequestPayload {
  action: 'APPROVED' | 'REJECTED';
  note?: string;
}

// ==========================================
// ACCUMULATED AMOUNT RESPONSES
// ==========================================

export interface ContributorAccumulatedAmount {
  acceptedAudioCount: number;
  payableCount: number;
  contributorCap: number;
  amountPerContribution: number;
  accumulatedAmount: number;
}

export type TestSubmissionStatus = 'PENDING' | 'VALIDATED' | 'REJECTED';

export interface ContributorTestSituation {
  status: TestSubmissionStatus | 'NONE';
  score?: number;
}

export interface ContributorPayoutSituation {
  threshold: number;
  ratePerContribution: number;
  contributorCap: number;
  acceptedAudioCount: number;
  totalUploaded: number;
  step1Reached: boolean;
  step1Remaining: number;
  step1Progress: number;
  tests: Record<string, ContributorTestSituation>;
  hasValidatedTest: boolean;
  baselineAccepted: number | null;
  validatedAudiosAfterTest: number;
  payableCount: number;
  accumulatedAmount: number;
  awaitingPayoutAmount: number;
  earnedAmount: number;
  canRequestPayout: boolean;
  hasPendingPayoutRequest: boolean;
  latestPayoutStatus: string | null;
}

export interface ModeratorAccumulatedAmount {
  validatedCount: number;
  accumulatedAmount: number;
  moderatorAmount: number;
  isDue: boolean;
  alreadyPaid: boolean;
  hasOpenRequestOrTransaction: boolean;
  periodMonth: number;
  periodYear: number;
}

// ==========================================
// COMMUNITY PROFILE PAYMENT (mobile money)
// ==========================================

export interface CommunityProfileAccumulatedAmount {
  profileId: number;
  profileCode: string;
  totalCount: number;
  pendingCount: number;
  rejectedCount: number;
  acceptedCount: number;
  acceptedDurationMs: number;
  alreadyPaidCount: number;
  alreadyPaidDurationMs: number;
  payableCount: number;
  payableDurationMs: number;
  amount: number;
  contributionAmount: number;
  recordedTimeAmount: number;
  compensationBasis: CommunityCompensationBasis;
  amountPerCommunityContribution: number;
  amountPerCommunityRecordedMinute: number;
  phoneNumber: string | null;
  network: PaymentNetwork | null;
}

export interface CommunityProfilePaymentResult {
  success: boolean;
  txReference: string;
  identifier: string;
  amount: number;
  contributionsPaid: number;
  recordingDurationMsPaid: number;
  compensationBasis: CommunityCompensationBasis;
}

// ==========================================
// ELIGIBLE MODERATORS
// ==========================================

export interface EligibleModerator {
  id: number;
  name: string;
  email: string;
  phoneNumber: string;
  validatedCount: number;
}

export interface EligibleModeratorsResponse {
  moderators: EligibleModerator[];
}

// ==========================================
// ADMIN PAYOUT REQUESTS RESPONSE
// ==========================================

export interface AdminPayoutRequestsPaginatedResponse {
  items: BackendPayoutRequest[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface AdminPayoutRequestsFullResponse {
  moderators: AdminPayoutRequestsPaginatedResponse;
  contributors: AdminPayoutRequestsPaginatedResponse;
  summary: {
    totalPending: number;
    totalModeratorsPending: number;
    totalContributorsPending: number;
    totalModeratorsAmount: number;
    totalContributorsAmount: number;
  };
}

// ==========================================
// PAY ALL MODERATORS RESPONSE
// ==========================================

export interface PayAllModeratorsResponse {
  success: number;
  failed: number;
  total: number;
}
// ==========================================
// COLLECTOR PAYMENT HISTORY
// ==========================================

export type CollectorPaymentDirection = "sent" | "received";

export interface CollectorPaymentHistoryItem {
  id: number;
  amount: number;
  amountPaid: number | null;
  status: string;
  network: string | null;
  phoneNumber: string | null;
  profileCode: string | null;
  contributionsPaid: number | null;
  createdAt: string;
  processedAt: string | null;
}

export interface CollectorPaymentHistoryResponse {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  data: CollectorPaymentHistoryItem[];
}
